import React, { useState, useEffect } from 'react';
import { signInWithCredential, GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth, db } from '../../config/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../context/AlertContext';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Dumbbell } from 'lucide-react';

import { Helmet } from 'react-helmet-async';

import { loginSchema, registerSchema } from '../../utils/validators';

const LoginScreen = () => {
    // ... (keep check for t, showAlert, hooks)
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(() => window.location.pathname === '/register');
    const [isLoading, setIsLoading] = useState(false);

    // Check for pending import routine
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const importId = urlParams.get('importRoutine');
        if (importId) {
            localStorage.setItem('pendingImportRoutine', importId);
        }
    }, []);

    // Initialize Google Auth plugin on mobile
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            GoogleAuth.initialize({
                clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
                scopes: ['profile', 'email'],
                grantOfflineAccess: true,
            });
        }
    }, []);

    const handleGoogleSignIn = async () => {
        try {
            setIsLoading(true);

            if (Capacitor.isNativePlatform()) {
                // Use native Google Auth plugin on mobile
                const googleUser = await GoogleAuth.signIn();

                // Create Firebase credential from Google token
                const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);

                // Sign in to Firebase with the credential
                await signInWithCredential(auth, credential);
            } else {
                // Use popup on web
                const provider = new GoogleAuthProvider();
                const result = await signInWithPopup(auth, provider);

                // CHECK FOR INVITE REF
                const urlParams = new URLSearchParams(window.location.search);
                const trainerRef = urlParams.get('ref');
                if (trainerRef && result.user) {
                    // Only set if not already set (don't overwrite existing trainer if they log in again via link)
                    // But merge: true is safe. However, getDoc check is better to avoid overwriting a different trainer?
                    // No, if they use a new link, maybe they want to switch? 
                    // Let's just merge for now as this is "Sign In / Sign Up" flow.
                    await setDoc(doc(db, 'users', result.user.uid), {
                        trainerId: trainerRef
                    }, { merge: true });
                }
            }
        } catch (error) {
            console.error('Error signing in with Google:', error);
            if (error.code !== 'auth/popup-closed-by-user' && error !== 'popup_closed_by_user') {
                showAlert(`${t('common.login_error')}: ${error.message || JSON.stringify(error)}`, { type: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailSignIn = async (e) => {
        e.preventDefault();

        // Validation
        const validation = loginSchema.safeParse({ email, password });
        if (!validation.success) {
            showAlert(validation.error.issues?.[0]?.message || "Validation Error", { type: 'warning' });
            return;
        }

        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (error) {
            // ... (keep error handling)
            console.error('Error signing in with email:', error);
            let errorMessage = t('common.login_error');

            if (error.code === 'auth/wrong-password') {
                errorMessage = 'Contraseña incorrecta.';
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = 'Usuario no encontrado.';
            } else if (error.code === 'auth/invalid-credential') {
                errorMessage = 'Email o contraseña incorrectos.';
            }

            showAlert(errorMessage, { type: 'error' });
        }
    };

    const handleEmailSignUp = async (e) => {
        e.preventDefault();

        // Validation
        const validation = registerSchema.safeParse({ email, password });
        if (!validation.success) {
            const errorMsg = validation.error.issues?.[0]?.message || "Validation Error";
            // Map validation errors to translations if possible (rudimentary mapping)
            let translatedError = errorMsg;
            if (errorMsg.includes("mayúscula")) translatedError = t('auth.password_needs_uppercase');
            if (errorMsg.includes("carácter especial")) translatedError = t('auth.password_needs_symbol');
            if (errorMsg.includes("6 caracteres")) translatedError = t('auth.password_too_short');

            showAlert(translatedError, { type: 'warning' });
            return;
        }

        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // ... (keep rest of logic)
            // Send verification email
            try {
                await sendEmailVerification(userCredential.user);

                // CHECK FOR INVITE REF
                const urlParams = new URLSearchParams(window.location.search);
                const trainerRef = urlParams.get('ref');
                if (trainerRef) {
                    await setDoc(doc(db, 'users', userCredential.user.uid), {
                        trainerId: trainerRef,
                        email: email, // Validated email
                        createdAt: new Date()
                    }, { merge: true });
                }

                showAlert(
                    '¡Cuenta creada! Te hemos enviado un email de verificación.\n\n⚠️ IMPORTANTE: Revisa tu carpeta de SPAM si no lo ves en la bandeja de entrada.',
                    { type: 'success', title: 'Aviso' }
                );
            } catch (verificationError) {
                console.error('❌ Error sending verification email:', verificationError);
                showAlert(
                    'Cuenta creada, pero hubo un error al enviar el email de verificación. Puedes reenviarlo desde tu perfil.',
                    { type: 'warning' }
                );
            }

            // Switch back to login mode
            setIsSignUp(false);
            setEmail('');
            setPassword('');
        } catch (error) {
            // ... (keep error handling)
            console.error('❌ Error creating account:', error);
            let errorMessage = 'Error al crear la cuenta.';

            if (error.code === 'auth/email-already-in-use') {
                errorMessage = 'Este email ya está registrado.';
            } else if (error.code === 'auth/weak-password' || error.code === 'auth/password-does-not-meet-requirements') {
                errorMessage = t('auth.password_weak');
            } else if (error.code === 'auth/invalid-email') {
                errorMessage = 'Email inválido.';
            }
            showAlert(errorMessage, { type: 'error' });
        }
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-black p-4 relative overflow-hidden">
            <Helmet>
                <title>{isSignUp ? t('auth.register_title') : t('auth.login_title')} - Gym Tracker Pro</title>
                <meta name="description" content={t('auth.login_desc')} />
            </Helmet>

            <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-red-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="relative z-10 w-full max-w-sm flex flex-col gap-8 items-center animate-in fade-in slide-in-from-bottom-8 duration-700">
                {/* Typography - Giant Size, Tight Tracking */}
                <div className="text-center flex flex-col items-center">
                    <div className="bg-red-600 p-4 rounded-3xl shadow-[0_0_30px_rgba(220,38,38,0.6)] -rotate-12 mb-8 animate-in zoom-in duration-500 overflow-hidden transform-gpu border border-red-600">
                        <Dumbbell size={64} className="text-white rotate-45" strokeWidth={3} />
                    </div>
                    <h1 className="text-4xl md:text-6xl font-black text-white italic tracking-tighter leading-none">
                        GYM TRACKER <span className="text-red-600">PRO</span>
                    </h1>
                    <p className="text-neutral-400 text-sm mt-2 font-bold">
                        {isSignUp ? t('auth.create_account_title') : t('auth.login_title_action')}
                    </p>
                    {new URLSearchParams(window.location.search).get('ref') && isSignUp && (
                        <div className="mt-2 bg-blue-900/30 text-blue-400 text-xs font-bold py-1 px-3 rounded-full border border-blue-900/50 inline-block">
                            {t('auth.trainer_invite_applied')}
                        </div>
                    )}
                </div>

                {/* Email/Password Form */}
                <form onSubmit={isSignUp ? handleEmailSignUp : handleEmailSignIn} className="w-full max-w-xs flex flex-col gap-4">
                    <input
                        type="email"
                        name="email_field"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('auth.email_placeholder')}
                        className="bg-neutral-900 border border-neutral-800 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-red-600 transition-colors"
                        required
                    />
                    <div className="w-full">
                        <input
                            type="password"
                            name="password_field"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={isSignUp ? t('auth.password_requirements_hint') : t('auth.password_placeholder_min')}
                            className="w-full bg-neutral-900 border border-neutral-800 text-white px-4 py-3 rounded-xl focus:outline-none focus:border-red-600 transition-colors"
                            required
                            minLength={6}
                        />
                        {isSignUp && (
                            <p className="text-[10px] text-neutral-500 mt-1 ml-2">
                                * {t('auth.password_requirements_hint')}
                            </p>
                        )}
                        {!isSignUp && (
                            <div className="flex justify-end mt-1">
                                <a href="/forgot-password" className="text-[10px] text-neutral-500 hover:text-white transition-colors uppercase font-bold tracking-wider">
                                    {t('auth.forgot_password', '¿Olvidaste tu contraseña?')}
                                </a>
                            </div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className="w-full bg-red-600 text-white px-8 py-4 rounded-full font-bold text-sm tracking-wide hover:bg-red-700 hover:shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-all transform active:scale-95 uppercase"
                    >
                        {isSignUp ? t('auth.register_title') : t('auth.login_title')}
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setIsSignUp(!isSignUp);
                            setEmail('');
                            setPassword('');
                        }}
                        className="text-neutral-400 text-sm hover:text-red-500 transition-colors"
                    >
                        {isSignUp ? t('auth.switch_to_login') : t('auth.switch_to_register')}
                    </button>
                </form>

                {/* Divider */}
                <div className="flex items-center gap-4 w-full max-w-xs">
                    <div className="flex-1 h-px bg-neutral-800"></div>
                    <span className="text-neutral-500 text-sm font-bold">o</span>
                    <div className="flex-1 h-px bg-neutral-800"></div>
                </div>

                {/* Google Button - Dark Mode Style */}
                <button
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="w-full max-w-xs bg-neutral-900 border border-neutral-800 text-white px-8 py-4 rounded-full font-bold text-sm tracking-wide hover:bg-neutral-800 hover:border-red-600 hover:shadow-[0_0_20px_rgba(220,38,38,0.2)] transition-all transform active:scale-95 flex items-center justify-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <svg className="w-5 h-5 text-neutral-400 group-hover:text-white transition-colors" viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        />
                        <path
                            fill="currentColor"
                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        />
                        <path
                            fill="currentColor"
                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        />
                        <path
                            fill="currentColor"
                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        />
                    </svg>
                    <span className="font-black uppercase group-hover:text-red-500 transition-colors">
                        {isLoading ? t('auth.authenticating') : t('auth.google_login')}
                    </span>
                </button>
            </div >
        </div >
    );
};

export default LoginScreen;
